<template>
    <div class="">
        <!-- DataTable Component -->
        <DataTable
            :perPage="itemsPerPage"
            :columns="displayColumns"
            :items="paginatedCategories"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableDragDrop="false"
            :enableMultiSelect="true"
            @page-changed="handlePageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @sort="handleSort"
        >
            <!-- Toolbar Slot -->
            <template #toolbar>
                <div class="ppv2-flex ppv2-justify-between ppv2-items-center ppv2-mb-6">
                    <div class="ppv2-flex ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                        </div>
                    </div>

                    <!-- Create Button -->
                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="handleCreate"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular">{{ $t('productCategories.actions.create') }}</span>
                    </BaseButton>
                </div>
            </template>

            <!-- Column: Name -->
            <template #column-name="{ item }">
                <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                    <div v-if="item.image" class="ppv2-w-10 ppv2-h-10 ppv2-rounded-lg ppv2-overflow-hidden ppv2-bg-gray-100">
                        <img :src="item.image" class="ppv2-w-full ppv2-h-full ppv2-object-cover" />
                    </div>
                    <div
                        v-else
                        class="ppv2-w-10 ppv2-h-10 ppv2-rounded-lg ppv2-bg-gray-200 ppv2-flex !ppv2-items-center !ppv2-justify-center"
                    >
                        <LayoutGrid :size="20" class="ppv2-text-gray-400" />
                    </div>
                    <button
                        v-if="!currentCategory"
                        @click="drillDown(item)"
                        class="ppv2-text-black poppinsRegular hover:ppv2-text-[#7D2DBD] ppv2-underline ppv2-text-left"
                        :style="{ fontSize: normalizeFontSize(14) }"
                    >
                        {{ item.name }}
                    </button>
                    <span v-else class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                        {{ item.name }}
                    </span>
                </div>
            </template>

            <!-- Column: Products Count -->
            <template #column-products_count="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.products_count || 0 }}
                </span>
            </template>
            <!-- Column: Subcategories Count -->
            <template #column-subcategories_count="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.subcategories_count || 0 }}
                </span>
            </template>

            <!-- Column: Visibility (État) -->
            <template #column-is_visible="{ item }">
                <button
                    @click="toggleVisibility(item)"
                    class="ppv2-min-w-[125px] ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-transition-all"
                    :class="
                        item.is_visible
                            ? 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                            : 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                    "
                >
                    <div
                        class="ppv2-w-9 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all"
                        :class="item.is_visible ? 'ppv2-bg-[#206A64] ppv2-justify-end' : 'ppv2-bg-[#4E4E4E] ppv2-justify-start'"
                    >
                        <div class="ppv2-w-3.5 ppv2-h-3.5 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5"></div>
                    </div>
                    <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                        {{ item.is_visible ? $t('productCategories.status.active') : $t('productCategories.status.inactive') }}
                    </span>
                </button>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div
                    :style="{ fontSize: normalizeFontSize(14) }"
                    class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                >
                    <button
                        @click="handleEdit(item)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('productCategories.actions.edit')"
                    >
                        <SquarePen :size="18" />
                    </button>
                    <button
                        class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('productCategories.actions.delete')"
                        @click="deleteSingleItem(item.id)"
                    >
                        <Trash2 :size="18" />
                    </button>
                </div>
            </template>

            <!-- Bulk Actions Slot -->
            <template #bulk-actions="{ selectedCount }">
                <span :style="{ fontSize: normalizeFontSize(15) }" class="poppinsRegular ppv2-text-black">
                    {{ selectedCount }} élément{{ selectedCount > 1 ? 's' : '' }} sélectionné{{ selectedCount > 1 ? 's' : '' }}
                </span>
                <span class="poppinsRegular ppv2-text-white ppv2-text-opacity-50" :style="{ fontSize: normalizeFontSize(15) }"
                    >|</span
                >
                <BaseButton
                    variant="outline"
                    @click="deleteSelectedItems"
                    customClass="!ppv2-px-4 !ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Trash2 :size="14" />
                    <span>{{ $t('common.deleteSelection') }}</span>
                </BaseButton>
            </template>
        </DataTable>

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template slot="content">
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template slot="actions">
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-h-[45px] !ppv2-text-base !ppv2-min-w-[170px]`"
                >
                    {{ $t('productCategories.deletePopup.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-h-[45px] !ppv2-text-base !ppv2-min-w-[170px]`"
                >
                    {{ $t('productCategories.deletePopup.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- Create Category/Subcategory Popup -->
        <CategoryForm :parentCategory="currentCategory" @success="handleCreateSuccess" v-model="showCreatePopup" :token="token" />
        <!-- Edit Category/Subcategory Popup -->
        <CategoryForm
            v-if="showEditPopup"
            :editItem="editingItem"
            @success="handleEditSuccess"
            v-model="showEditPopup"
            :token="token"
        />
    </div>
</template>

<script>
import { Plus, Trash2, SquarePen, LayoutGrid } from 'lucide-vue'
import normalizeSize from '../../../../utils/normalizeSize'
import Search from '../../Experience/components/Search.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import DataTable from '../../../Shared/DataTable.vue'
import BaseInput from '../../../Shared/BaseInput.vue'
import BaseFileUpload from '../../../Shared/BaseFileUpload.vue'
import CategoryForm from './CategoryForm.vue'
import { eventBus } from '../../../Customer/eventBus'
import axios from 'axios'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../../utils/perPage'

export default {
    props: {
        userId: {
            type: [Number, String, null],
            required: false,
            default: null,
        },
        token: {
            type: String,
            default: '',
        },
    },
    name: 'CategorieTable',
    components: {
        Plus,
        SquarePen,
        Trash2,
        Search,
        BaseButton,
        CustomDialog,
        DataTable,
        LayoutGrid,
        BaseInput,
        BaseFileUpload,
        CategoryForm,
    },
    data() {
        return {
            searchQuery: '',
            currentPage: 1,
            itemsPerPage: getPerPage('product_categories_items_per_page', 10),
            selectedItems: [],
            selectAll: false,
            isLoading: false,
            error: null,
            currentCategory: null,
            orderBy: 'id',
            orderDirection: 'desc',
            tableColumns: [
                { key: 'name', label: this.$t('productCategories.table.name'), sortable: true, type: 'text' },
                {
                    key: 'products_count',
                    label: this.$t('productCategories.table.products_count'),
                    sortable: true,
                    type: 'number',
                },
                {
                    key: 'subcategories_count',
                    label: this.$t('productCategories.table.subcategories_count'),
                    sortable: true,
                    type: 'number',
                },
                {
                    key: 'is_visible',
                    label: this.$t('productCategories.table.status'),
                    sortable: true,
                    type: 'boolean',
                },
                {
                    key: 'actions',
                    label: this.$t('productCategories.table.actions'),
                    sortable: false,
                    type: 'actions',
                },
            ],
            showDeletePopup: false,
            deletePopupTitle: this.$t('productCategories.deletePopup.title'),
            deletePopupDescription: '',
            deleteAction: null, // 'single' or 'multiple'
            itemToDelete: null,
            categories: {
                data: [],
                total: 0,
            },
            createUrl: '/back-office/administration/categories/product/create',
            showCreatePopup: false,
            showEditPopup: false,
            editingItem: null,
        }
    },

    computed: {
        displayColumns() {
            if (this.currentCategory) {
                return this.tableColumns.filter((col) => col.key !== 'subcategories_count')
            }
            return this.tableColumns
        },
        filteredCategories() {
            return this.categories.data || []
        },
        paginatedCategories() {
            return this.filteredCategories
        },
        totalPages() {
            return this.categories.total
        },
    },
    mounted() {
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
        this.fetchCategories()
    },
    beforeDestroy() {
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        handlePageChange(page) {
            this.currentPage = page
            this.fetchCategories()
        },
        handleSearchUpdate(searchValue) {
            this.searchQuery = searchValue
            this.currentPage = 1
            this.fetchCategories()
        },
        async toggleVisibility(item) {
            const previousStatus = item.is_visible
            const newStatus = !previousStatus
            item.is_visible = newStatus

            try {
                const response = await axios.patch(
                    '/api/v3/product_category/update-visibility',
                    {
                        id: item.id,
                        type: this.currentCategory ? 'subcategory' : 'category',
                        is_visible: newStatus ? 1 : 0,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                            'Content-Type': 'application/json',
                        },
                    }
                )

                if (response.data.success) {
                    this.$toast.success(response.data.message)
                    // If we deactivated a category, we might want to refresh to see cascaded changes if we were in subcategory view
                    // but usually we are in the same view. If it's a category, subcategories are not visible here anyway.
                    // However, if we are in subcategory view and deactivate a subcategory, it's fine.
                    if (!newStatus) {
                        this.fetchCategories()
                    }
                } else {
                    item.is_visible = previousStatus
                    this.$toast.error('Erreur lors de la mise à jour de la visibilité')
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                item.is_visible = previousStatus
            }
        },
        handleSort(key) {
            if (this.orderBy === key) {
                this.orderDirection = this.orderDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.orderBy = key
                this.orderDirection = 'asc'
            }
            this.currentPage = 1
            this.fetchCategories()
        },

        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
            this.selectAll = this.selectedItems.length === this.paginatedCategories.length
        },

        toggleSelectAll() {
            const allIds = this.paginatedCategories.map((item) => item.id)
            const allSelected = allIds.length > 0 && allIds.every((id) => this.selectedItems.includes(id))
            if (allSelected) {
                this.selectedItems = []
            } else {
                this.selectedItems = [...allIds]
            }
        },

        async deleteSelectedItems() {
            if (this.selectedItems.length === 0) return

            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('productCategories.deletePopup.message')
            this.showDeletePopup = true
        },

        deleteSingleItem(itemId) {
            this.deleteAction = 'single'
            this.itemToDelete = itemId
            this.deletePopupDescription = this.$t('productCategories.deletePopup.message')
            this.showDeletePopup = true
        },

        async confirmDelete() {
            try {
                let response
                if (this.deleteAction === 'multiple') {
                    response = await axios.post(
                        '/api/v3/product_category/bulk-delete',
                        {
                            ids: this.selectedItems,
                            type: this.currentCategory ? 'subcategory' : 'category',
                        },
                        {
                            headers: {
                                Authorization: `Bearer ${this.token}`,
                                'Content-Type': 'application/json',
                            },
                        }
                    )
                    this.selectedItems = []
                    this.selectAll = false
                } else {
                    const type = this.currentCategory ? 'subcategory' : 'category'
                    response = await axios.delete(`/api/v3/product_category/delete/${type}/${this.itemToDelete}`, {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                            'Content-Type': 'application/json',
                        },
                    })
                }

                this.$toast.success(response.data.message || 'Supprimé avec succès')
                await this.fetchCategories()
                this.$emit('refresh-stats')
            } catch (error) {
                console.error('Delete error:', error)
                const message =
                    error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : 'Erreur lors de la suppression'
                this.$toast.error(message)
            }

            this.showDeletePopup = false
            this.deleteAction = null
            this.itemToDelete = null
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('product_categories_items_per_page', this.itemsPerPage)
            this.fetchCategories()
        },
        handleCreate() {
            this.showCreatePopup = true
        },
        handleCreateSuccess() {
            this.showCreatePopup = false
            this.fetchCategories()
            this.$emit('refresh-stats')
        },
        handleEdit(item) {
            this.editingItem = item
            this.showEditPopup = true
        },
        handleEditSuccess() {
            this.showEditPopup = false
            this.editingItem = null
            this.fetchCategories()
            this.$emit('refresh-stats')
        },
        openEditTab(itemId) {
            const path = this.currentCategory ? 'subcategories' : 'categories'
            window.location.href = `/back-office/administration/${path}/product/${itemId}/edit`
        },

        async fetchCategories() {
            this.isLoading = true
            this.error = null

            try {
                let url = `/api/v3/product_category/list?currentPage=${this.currentPage}&search=${this.searchQuery}&itemsPerPage=${this.itemsPerPage}&orderBy=${this.orderBy}&orderDirection=${this.orderDirection}`
                if (this.currentCategory) {
                    url += `&category_id=${this.currentCategory.id}`
                }

                const res = await fetch(url, {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                        'Content-Type': 'application/json',
                    },
                })
                const json = await res.json()
                this.categories = {
                    data: json.data || [],
                    total: json.last_page || 0,
                }
            } catch (err) {
                this.error = err.message
                console.error('API error:', err)
            } finally {
                this.isLoading = false
            }
        },
        drillDown(category) {
            this.currentCategory = category
            this.currentPage = 1
            this.searchQuery = ''
            this.fetchCategories()
            this.$emit('breadcrumb-change', category.name)
        },
        resetDrillDown() {
            this.currentCategory = null
            this.currentPage = 1
            this.searchQuery = ''
            this.fetchCategories()
            this.$emit('breadcrumb-change', null)
        },
    },
    watch: {
        selectedItems: {
            handler(newSelection) {
                const currentPageIds = this.paginatedCategories.map((item) => item.id)
                this.selectAll = currentPageIds.length > 0 && currentPageIds.every((id) => newSelection.includes(id))
            },
            deep: true,
        },

        currentPage() {
            this.selectedItems = []
            this.selectAll = false
        },

        filteredCategories() {
            this.selectedItems = []
            this.selectAll = false
        },
    },
}
</script>

<style scoped lang="scss">
.textGray {
    color: #1a1f37;
}

.dark {
    .textGray {
        color: #bfbfbf;
    }
}
</style>
